import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import type { TFunction } from 'i18next';
import { AlertTriangle, WifiOff } from 'lucide-react';
import { useConnectionStore } from '@/state/connection.store';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { cn } from '@/lib/cn';

// Reconnection UX per docs/ui/operator-ui-tds.md §11.5.
//
// A non-blocking banner shows while Socket.IO is reconnecting; the copy
// includes a relative timestamp from the Zustand connection store's
// lastUpdateAt. After 30s of failed reconnect, an escalation modal asks
// the operator to refresh.
//
// The redis-ui Pub/Sub backplane (NEX-69) means the operator's view picks
// up where it left off post-reconnect — there is no missed-event recovery
// code in the client per TDS §11.5.
//
// VITE_WS_DISABLE=true short-circuits the banner entirely: when the BFF
// WS surface (NEX-69 / NEX-72 /cases namespace) hasn't shipped, AuthBridge
// never opens the socket so `connected` stays at its initial `false`, and
// the banner would otherwise stay up forever. The flag tells the UI "we
// don't expect real-time updates here" — REST keeps working fine.

const TICK_INTERVAL_MS = 1_000;
const MODAL_AFTER_MS = 30_000;

function isWsDisabled(): boolean {
  return import.meta.env.VITE_WS_DISABLE === 'true' && import.meta.env.DEV;
}

function relativeAge(lastUpdateAt: number | null, t: TFunction<'admin'>): string {
  if (!lastUpdateAt) return t('time.justNow');
  const seconds = Math.max(1, Math.floor((Date.now() - lastUpdateAt) / 1000));
  if (seconds < 60) return t('time.secondsAgo', { n: seconds });
  const minutes = Math.floor(seconds / 60);
  return t('time.minutesAgo', { n: minutes });
}

export function ReconnectionBanner() {
  // Gate evaluated outside the hook-bearing inner component so React's
  // rules-of-hooks aren't violated when WS is intentionally off.
  if (isWsDisabled()) return null;
  return <ReconnectionBannerInner />;
}

function ReconnectionBannerInner() {
  const { t } = useTranslation('admin');
  const connected = useConnectionStore((s) => s.connected);
  const reconnectAttempts = useConnectionStore((s) => s.reconnectAttempts);
  const lastUpdateAt = useConnectionStore((s) => s.lastUpdateAt);
  const [disconnectedSince, setDisconnectedSince] = useState<number | null>(null);
  const [tick, setTick] = useState(0);
  const [modalOpen, setModalOpen] = useState(false);

  // Track when the connection dropped. Banner shows while reconnect attempts
  // are in progress; the escalation modal opens 30s after the drop.
  useEffect(() => {
    if (!connected && disconnectedSince === null) {
      setDisconnectedSince(Date.now());
    }
    if (connected && disconnectedSince !== null) {
      setDisconnectedSince(null);
      setModalOpen(false);
    }
  }, [connected, disconnectedSince]);

  // 1-second redraw cadence while disconnected so the "last update Ns ago"
  // copy stays current and we can check the 30s escalation threshold.
  useEffect(() => {
    if (connected || disconnectedSince === null) return;
    const id = window.setInterval(() => {
      setTick((t) => t + 1);
      if (Date.now() - disconnectedSince >= MODAL_AFTER_MS) {
        setModalOpen(true);
      }
    }, TICK_INTERVAL_MS);
    return () => window.clearInterval(id);
  }, [connected, disconnectedSince]);

  // Reference `tick` so React knows we depend on the 1s redraw cadence.
  void tick;

  const showBanner = !connected && disconnectedSince !== null;

  return (
    <>
      <div
        className={cn(
          'pointer-events-none fixed left-1/2 top-3 z-40 -translate-x-1/2 transition-opacity',
          showBanner ? 'opacity-100' : 'pointer-events-none opacity-0',
        )}
        aria-live="polite"
        role="status"
        data-testid="reconnection-banner"
      >
        <div className="flex items-center gap-2 rounded-full border border-amber-700/30 bg-amber-100 px-4 py-1.5 text-xs font-medium text-amber-900 shadow-md dark:bg-amber-900/40 dark:text-amber-100">
          <WifiOff className="h-3 w-3" aria-hidden />
          <span>
            {t('connection.reconnecting')}
            {reconnectAttempts > 0 ? t('connection.attempt', { n: reconnectAttempts }) : null}
            {' · '}
            {t('connection.lastUpdate', { age: relativeAge(lastUpdateAt, t) })}
          </span>
        </div>
      </div>

      <Dialog open={modalOpen} onOpenChange={setModalOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <AlertTriangle className="h-4 w-4 text-amber-600" aria-hidden />
              {t('connection.lost')}
            </DialogTitle>
            <DialogDescription>{t('connection.lostBody')}</DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button variant="outline" onClick={() => setModalOpen(false)}>
              {t('common:holds.actions.dismiss')}
            </Button>
            <Button onClick={() => window.location.reload()}>{t('connection.refresh')}</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
}
